import { LockClosedIcon } from '@heroicons/react/20/solid';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import { ShiftCard as ShiftCardData } from '../api/types';
import { shiftIdentity } from '../util/identity';

import StatusRibbon, { DependabilityRibbon } from './StatusRibbon';

// A single shift card, transcribed from the Figma "Shift" component:
//  ▸ quick-info: optional top row (Dependability / status ribbon + lock,
//    left-aligned), client title (ExtraBold, 2-line clamp), then
//    time · role · location.
//  ▸ footer: colour-coded "X/Y hired" badge + chevron.
// The card's colour band tracks its risk: critical → red, at_risk or a
// dependability drop → yellow, otherwise the neutral surface.

const formatTime = (iso: string, tz: string): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(iso))
    .replace(/\s/g, '')
    .toUpperCase();

export const formatTimeRange = (card: ShiftCardData): string => {
  if (!card.start_at || !card.end_at) return '';
  const tz = card.timezone || 'America/New_York';
  return `${formatTime(card.start_at, tz)}-${formatTime(card.end_at, tz)}`;
};

// The card's primary identity — the CLIENT. Genuinely shared with the Shift
// Detail panel now (see util/identity), so a card and the panel it opens cannot
// name different things. It used to prefer the venue label while the panel used
// the client, which is exactly what GRAVY-10340 reported.
export const cardIdentity = shiftIdentity;

// A shift with NO band — unfilled and already over (GRAVY-10352) — lands on
// `level-normal` with the Filled shifts. That is the intended reading: it is
// not urgent, because there is nothing left to fill.
const cardLevel = (card: ShiftCardData): string => {
  if (card.fill_status === 'critical') return 'level-critical';
  if (card.fill_status === 'at_risk' || card.dependability_drop)
    return 'level-warn';
  return 'level-normal';
};

// `onSelect` opens the Shift Detail panel in place of navigating; the calendar
// passes it so a click surfaces the panel (per the Shift Detail ticket). When
// omitted the card falls back to a link to the job detail.
type Props = { card: ShiftCardData; onSelect?: (shiftId: number) => void };

const ShiftCard = ({ card, onSelect }: Props) => {
  const hasTopRow =
    card.dependability_drop || !!card.ribbon || card.restricted_pool;
  const to = card.order_id
    ? `/orders/${card.order_id}/jobs/${card.job_id}`
    : '#';

  const inner = (
    <>
      <div className="dashv2-quickinfo">
        {hasTopRow && (
          <div className="dashv2-card-toprow">
            {card.dependability_drop && <DependabilityRibbon />}
            {card.ribbon && <StatusRibbon ribbon={card.ribbon} />}
            {/* role="img" is required for the aria-label to be exposed: on a
                bare span (implicit generic role) screen readers ignore it, so
                this icon-only state was announcing nothing. */}
            {card.restricted_pool && (
              <span
                className="dashv2-lock"
                role="img"
                aria-label="Restricted worker pool"
              >
                <LockClosedIcon />
              </span>
            )}
          </div>
        )}

        <div className="dashv2-card-body">
          {/* Identity rule: the client, falling back to the venue label only
              when the job has no customer. Never the billing account — its
              internal names ("Billing Account 2") say nothing about who owns
              the shift. `title` exposes the full string, since the heading is
              clamped to two lines. */}
          <h3 className="dashv2-card-title" title={cardIdentity(card)}>
            {cardIdentity(card)}
          </h3>
          <div className="dashv2-card-meta">
            <p className="dashv2-card-time">{formatTimeRange(card)}</p>
            {card.skill_name && (
              <p className="dashv2-card-role">{card.skill_name}</p>
            )}
            {card.location_code && (
              <p className="dashv2-card-loc">{card.location_code}</p>
            )}
          </div>
        </div>
      </div>

      <div className="dashv2-card-footer">
        {/* `unknown` where the server sends no band, matching the Day card:
            the fraction renders in the neutral tone rather than in a colour
            that would claim an urgency nobody stated. */}
        <span
          className={`dashv2-hired status-${card.fill_status ?? 'unknown'}`}
        >
          {card.hired_count}/{card.quantity} hired
        </span>
        <span className="dashv2-chevron">
          <ChevronRightIcon />
        </span>
      </div>
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        className={`dashv2-card ${cardLevel(card)}`}
        id={`dashv2-shift-${card.id}`}
        onClick={() => onSelect(card.id)}
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      className={`dashv2-card ${cardLevel(card)}`}
      to={to}
      id={`dashv2-shift-${card.id}`}
    >
      {inner}
    </Link>
  );
};

export default ShiftCard;
